import { create } from 'zustand';
import { applicationReviewsService } from 'src/services/application-reviews';
import type { RejectRequest, AcceptRequest, RejectResponse } from 'src/services/application-reviews';

export interface ApplicationReviewResponse {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: number;
  priceId: number;
  reviewedBy: number;
  reason: string;
  conclusion: string;
  note: string;
  application: any; // слишком сложная структура, используем any
  user: any; // можем типизировать позже
}

interface ApplicationReviewsState {
  items: ApplicationReviewResponse[];
  current: ApplicationReviewResponse | null;
  loading: boolean;
	loadingDetails: boolean;
  error: string | null;

  // filters / paging / sorting
  status?: number | null;
  search: string;
  offset: number;
  limit: number;
  column: 'id' | 'firstName' | 'lastName' | 'createdAt' | 'updatedAt';
  order: 'asc' | 'desc';

  setStatus: (status?: number | null) => void;
  setSearch: (v: string) => void;
  setPage: (page: number) => void;
  setSort: (column: ApplicationReviewsState['column'], order: ApplicationReviewsState['order']) => void;

  loadList: () => Promise<void>;
  loadOne: (id: string | number) => Promise<void>;
  reject: (id: string | number, data: RejectRequest) => Promise<RejectResponse>;
  accept: (id: string | number, data: AcceptRequest) => Promise<RejectResponse>;
}

export const useApplicationReviewsStore = create<ApplicationReviewsState>((set, get) => ({
  items: [],
  current: null,
  loading: false,
  loadingDetails: false,
  error: null,

  status: undefined,
  search: '',
  offset: 0,
  limit: 10,
  column: 'id',
  order: 'asc',

  setStatus: (status) => set({ status, offset: 0 }),
  setSearch: (v) => set({ search: v, offset: 0 }),
  setPage: (page) => set({ offset: Math.max(0, (page - 1) * get().limit) }),
  setSort: (column, order) => set({ column, order, offset: 0 }),

  loadList: async () => {
    set({ loading: true, error: null });
    try {
      const { status, search, offset, limit, column, order } = get();

      // Фильтруем параметры для передачи только валидных значений
      const params: any = {
        offset,
        limit,
        column,
        order
      };

      // Добавляем статус только если он определен
      if (status !== undefined) {
        params.status = status;
      }

      if (status === null) {
        params.status = '[1,-1]';
      }

      // Добавляем поиск только если он больше или равен 2 символам
      if (search && search.length >= 2) {
        params.search = search;
      }
      
      const data = await applicationReviewsService.list(params);
      set({ items: data?.data });
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to load application reviews' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  loadOne: async (id) => {
    set({ loadingDetails: true, error: null });
    try {
      const data = await applicationReviewsService.getOne(id);
      set({ current: data });
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to load application review' });
      throw error;
    } finally {
      set({ loadingDetails: false });
    }
  },

  reject: async (id, data) => {
    set({ loading: true, error: null });
    try {
      const result = await applicationReviewsService.reject(id, data);
      // Обновляем элемент в списке, если он существует
      set({
        items: get().items.map((item) => (item.id === id ? { ...item, conclusion: result.conclusion, note: result.note, status: result.status, updatedAt: result.updatedAt } : item)),
        current: (() => {
          const current = get().current;
          return current && current.id === id ? { ...current, conclusion: result.conclusion, note: result.note, status: result.status, updatedAt: result.updatedAt } : current;
        })(),
      });
      return result;
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to reject application review' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  accept: async (id, data) => {
    set({ loading: true, error: null });
    try {
      const result = await applicationReviewsService.accept(id, data);
      // Обновляем элемент в списке, если он существует
      set({
        items: get().items.map((item) => (item.id === id ? { ...item, conclusion: result.conclusion, note: result.note, status: result.status, priceId: result.priceId, updatedAt: result.updatedAt } : item)),
        current: (() => {
          const current = get().current;
          return current && current.id === id ? { ...current, conclusion: result.conclusion, note: result.note, status: result.status, priceId: result.priceId, updatedAt: result.updatedAt } : current;
        })(),
      });
      return result;
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to accept application review' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
}));
